import {
  ChoiceConfig,
  FormSchema,
  FormTemplateField,
  ImageUploadConfig,
  LongTextConfig,
  NumberConfig,
  ShortTextConfig,
} from "../types/formTemplates";
import { isTombstonedPointer, readPointerS3Key } from "./formImagePointer";

export interface AnswerValidationError {
  fieldId: string;
  message: string;
}

export interface AnswerValidationResult {
  ok: boolean;
  errors: AnswerValidationError[];
  errorsByFieldId: Record<string, string>;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * S3 key prefix guard for image_upload / signature fields. Mirrors the
 * backend's ``validate_upload_key_belongs_to``.
 *
 * Accepts EITHER of two layouts under the same salon + client root —
 * both KMS-encrypted PHI:
 *
 *   - form-upload prefix:
 *       phi/{salon}/documents/clients/{client}/forms/{formId}/{fieldId}/{ts}-{name}
 *
 *   - salon_documents version prefix:
 *       phi/{salon}/documents/clients/{client}/{document_key}/v{n}.{ext}
 *     (used after markup-save lands a new version — the form pointer's
 *     `s3_key` is swapped to this path so the form's inline preview
 *     flips to the marked-up version.)
 *
 * Both share `phi/{salon}/documents/clients/{client}/`, which is the
 * actual cross-tenant guarantee.
 */
export function isValidPhiUploadKey(
  key: unknown,
  salonUuid: string,
  clientUuid: string,
): boolean {
  if (typeof key !== "string") return false;
  return key.startsWith(`phi/${salonUuid}/documents/clients/${clientUuid}/`);
}

interface ValidateAnswersOptions {
  schema: FormSchema;
  data: Record<string, unknown>;
  /**
   * When true, required fields must be present and non-empty (submit).
   * When false, only value-shape checks run (draft save).
   */
  strict: boolean;
  /**
   * If set ("client" or "staff"), the `required` check only fires for
   * fields whose actor matches. Lets the staff "Submit form" button
   * succeed on a partial mixed-actor form (state will transition to
   * awaiting_client on the backend). Value-shape checks (type, options,
   * length) still run on every field regardless. Mirrors the backend's
   * `required_actor` kwarg on `validate_answer_data_against_schema`.
   */
  requiredActor?: "client" | "staff";
  /** Used to validate upload-key prefixes. */
  salonUuid?: string;
  clientUuid?: string;
}

/**
 * Field actor defaults to "staff" when omitted — matches the admin
 * form-builder default (`createDefaultField` in
 * `src/components/formTemplates/constants.ts`) and the backend
 * `_field_actor` helper.
 */
function fieldActor(field: FormTemplateField): "client" | "staff" {
  return field.actor === "client" ? "client" : "staff";
}

function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

function validateField(
  field: FormTemplateField,
  value: unknown,
  opts: ValidateAnswersOptions,
): string | null {
  // Layout-only fields never have a value; the data_json key shouldn't
  // exist (caller is responsible for stripping before sending).
  if (
    field.type === "section_header" ||
    field.type === "section_description" ||
    field.type === "divider" ||
    field.type === "page_break"
  ) {
    if (value !== undefined) return "Layout fields cannot have a value.";
    return null;
  }

  const empty = isEmpty(value);
  if (empty) {
    const requiredForThisActor =
      field.required &&
      (opts.requiredActor === undefined ||
        fieldActor(field) === opts.requiredActor);
    if (opts.strict && requiredForThisActor) return "This field is required.";
    return null; // lenient: empty is fine on draft (or for other-actor field)
  }

  switch (field.type) {
    case "short_text":
    case "long_text": {
      if (typeof value !== "string") return "Must be text.";
      const cfg = (field.config ?? {}) as Partial<
        ShortTextConfig | LongTextConfig
      >;
      if (
        cfg.maxLength !== null &&
        cfg.maxLength !== undefined &&
        value.length > cfg.maxLength
      ) {
        return `Must be ${cfg.maxLength} characters or fewer.`;
      }
      return null;
    }
    case "number": {
      if (typeof value !== "number" || Number.isNaN(value)) {
        return "Must be a number.";
      }
      const cfg = (field.config ?? {}) as Partial<NumberConfig>;
      if (cfg.min !== null && cfg.min !== undefined && value < cfg.min) {
        return `Must be at least ${cfg.min}.`;
      }
      if (cfg.max !== null && cfg.max !== undefined && value > cfg.max) {
        return `Must be at most ${cfg.max}.`;
      }
      return null;
    }
    case "date": {
      if (typeof value !== "string" || !DATE_RE.test(value)) {
        return "Must be a date in YYYY-MM-DD format.";
      }
      return null;
    }
    case "single_choice":
    case "dropdown": {
      if (typeof value !== "string") return "Pick one option.";
      const cfg = (field.config ?? { options: [] }) as Partial<ChoiceConfig>;
      const options = cfg.options ?? [];
      if (!options.some((o) => o.id === value)) {
        return "Selection is not one of the allowed options.";
      }
      return null;
    }
    case "multiple_choice": {
      if (!Array.isArray(value)) return "Pick one or more options.";
      const cfg = (field.config ?? { options: [] }) as Partial<ChoiceConfig>;
      const allowed = new Set((cfg.options ?? []).map((o) => o.id));
      for (const v of value) {
        if (typeof v !== "string" || !allowed.has(v)) {
          return "Selection contains an option that isn't allowed.";
        }
      }
      return null;
    }
    case "signature": {
      if (typeof value !== "string") return "Signature must be a file key.";
      if (
        opts.salonUuid &&
        opts.clientUuid &&
        !isValidPhiUploadKey(value, opts.salonUuid, opts.clientUuid)
      ) {
        return "Signature upload key has the wrong PHI prefix.";
      }
      return null;
    }
    case "image_upload": {
      // Accept three on-disk shapes, mirroring the backend's
      // validate_submitted_upload_keys (pointer-aware as of PR #340):
      //   - bare string s3_key (legacy single-image)
      //   - array of bare-string s3_keys (PR #295 multi-image)
      //   - array of pointer dicts (post-dual-write)
      // Tombstoned pointer entries (deleted_at set) are history, not
      // active answers, so they're skipped by both maxFiles enforcement
      // and prefix validation.
      const cfg = (field.config ?? {}) as Partial<ImageUploadConfig>;
      const entries = Array.isArray(value) ? value : [value];
      const activeEntries = entries.filter(
        (e) => e !== null && e !== undefined && !isTombstonedPointer(e),
      );
      if (cfg.maxFiles && activeEntries.length > cfg.maxFiles) {
        return `At most ${cfg.maxFiles} file${cfg.maxFiles === 1 ? "" : "s"}.`;
      }
      for (const entry of activeEntries) {
        const key = readPointerS3Key(entry);
        if (typeof key !== "string" || !key) {
          return "Image upload value missing an S3 key.";
        }
        if (
          opts.salonUuid &&
          opts.clientUuid &&
          !isValidPhiUploadKey(key, opts.salonUuid, opts.clientUuid)
        ) {
          return "Image upload key has the wrong PHI prefix.";
        }
      }
      return null;
    }
    default: {
      const _exhaustive: never = field;
      return null;
    }
  }
}

export function validateAnswers(
  opts: ValidateAnswersOptions,
): AnswerValidationResult {
  const errors: AnswerValidationError[] = [];
  const byId: Record<string, string> = {};

  const fieldsById = new Map<string, FormTemplateField>();
  for (const f of opts.schema.fields) fieldsById.set(f.id, f);

  // Reject unknown field ids (backend does this too).
  for (const key of Object.keys(opts.data)) {
    if (!fieldsById.has(key)) {
      const msg = `No such field: ${key}`;
      errors.push({ fieldId: key, message: msg });
      byId[key] = msg;
    }
  }

  for (const field of opts.schema.fields) {
    const message = validateField(field, opts.data[field.id], opts);
    if (message) {
      errors.push({ fieldId: field.id, message });
      byId[field.id] = message;
    }
  }

  return { ok: errors.length === 0, errors, errorsByFieldId: byId };
}

/**
 * Strip out layout-only field ids from a data object before sending to
 * the backend (backend rejects them).
 */
export function stripLayoutFieldValues(
  schema: FormSchema,
  data: Record<string, unknown>,
): Record<string, unknown> {
  const layoutIds = new Set(
    schema.fields
      .filter(
        (f) =>
          f.type === "section_header" ||
          f.type === "section_description" ||
          f.type === "divider" ||
          f.type === "page_break",
      )
      .map((f) => f.id),
  );
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(data)) {
    if (!layoutIds.has(k)) out[k] = v;
  }
  return out;
}
